import {
  resolveMetricCategory,
  type useMetricDirectory,
} from "@/metrics/registry"
import type { SortDescriptor } from "@coval/ui"

import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import {
  getTagGroupKey,
  METRICS_GROUPING_OPTION,
  METRICS_SORT_OPTION,
  UNTAGGED_GROUP_KEY,
  type GroupingOption,
  type SortOption,
} from "../constants"
import type { GroupableMetric, MetricGroup } from "../types"

// Short blurbs shown after the count on category group headers. Keyed by the
// functional category label from the metric catalog; unknown categories simply
// render without a description.
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "Audio Fidelity": "Audio clarity, artifacts, and signal quality",
  "Compliance & Scripting":
    "Adherence to required scripts, policies, and flows",
  "Conversation Quality":
    "Coherence, helpfulness, and correctness of responses",
  "LLM Judge": "Metrics scored by an LLM evaluator",
  "Responsiveness & Timing": "Latency, response speed, and turn timing",
  Sentiment: "Emotional tone and sentiment of the conversation",
  "Speech Naturalness": "How natural and human-like the speech sounds",
  "Task Resolution": "Whether the agent completes the user's task",
  "Trace & Performance": "System traces, token usage, and performance costs",
  Other: "Uncategorized metrics",
}

type ByManager = ReturnType<typeof useMetricDirectory>["byManager"]

// Sort metrics by display name (alphabetically).
function sortMetricsByName<T extends GroupableMetric>(
  metricsToSort: T[],
  direction: SortDescriptor["direction"],
): T[] {
  return [...metricsToSort].sort((a, b) => {
    const nameA = resolveMetricDisplayName(
      a.metric_display_name,
      a.metric_name,
    ).toLowerCase()
    const nameB = resolveMetricDisplayName(
      b.metric_display_name,
      b.metric_name,
    ).toLowerCase()
    const comparison = nameA.localeCompare(nameB)
    return direction === "desc" ? comparison * -1 : comparison
  })
}

// Sort metrics by created date. Built-in metrics without a date sink to the end.
function sortMetricsByCreated<T extends GroupableMetric>(
  metricsToSort: T[],
  direction: SortDescriptor["direction"],
): T[] {
  return [...metricsToSort].sort((a, b) => {
    if (!a.created_at && !b.created_at) {
      return 0
    }
    if (!a.created_at) {
      return 1
    }
    if (!b.created_at) {
      return -1
    }
    const dateA = new Date(a.created_at).getTime()
    const dateB = new Date(b.created_at).getTime()
    return direction === "asc" ? dateA - dateB : dateB - dateA
  })
}

// Sort metrics by updated date. Built-in metrics without a date sink to the end.
function sortMetricsByUpdated<T extends GroupableMetric>(
  metricsToSort: T[],
  direction: SortDescriptor["direction"],
): T[] {
  return [...metricsToSort].sort((a, b) => {
    if (!a.last_updated_at && !b.last_updated_at) {
      return 0
    }
    if (!a.last_updated_at) {
      return 1
    }
    if (!b.last_updated_at) {
      return -1
    }
    const dateA = new Date(a.last_updated_at).getTime()
    const dateB = new Date(b.last_updated_at).getTime()
    return direction === "asc" ? dateA - dateB : dateB - dateA
  })
}

// Apply the given sort descriptor to a list of metrics.
function sortMetrics<T extends GroupableMetric>(
  metricsToSort: T[],
  sortDescriptor: SortDescriptor,
): T[] {
  const direction = sortDescriptor.direction
  switch (sortDescriptor.columnId as SortOption) {
    case METRICS_SORT_OPTION.NAME:
      return sortMetricsByName(metricsToSort, direction)
    case METRICS_SORT_OPTION.CREATED:
      return sortMetricsByCreated(metricsToSort, direction)
    case METRICS_SORT_OPTION.UPDATED:
    default:
      return sortMetricsByUpdated(metricsToSort, direction)
  }
}

// "Other" always sorts last; everything else is alphabetical by label.
function sortGroupsByLabel<T extends GroupableMetric>(
  a: MetricGroup<T>,
  b: MetricGroup<T>,
): number {
  if (a.label === "Other") {
    return 1
  }
  if (b.label === "Other") {
    return -1
  }
  return a.label.localeCompare(b.label)
}

interface BuildMetricGroupsParams<T extends GroupableMetric> {
  metrics: T[]
  grouping: GroupingOption
  byManager: ByManager
  // Sort applied to the metrics within each group.
  sortDescriptor: SortDescriptor
}

// Group and sort metrics for a metrics view. Shared by the list page's table
// and card views and by the metric-selection modal so they stay in sync on
// grouping semantics and ordering.
export function buildMetricGroups<T extends GroupableMetric>({
  metrics,
  grouping,
  byManager,
  sortDescriptor,
}: BuildMetricGroupsParams<T>): MetricGroup<T>[] {
  const sort = (list: T[]) => sortMetrics(list, sortDescriptor)

  switch (grouping) {
    case METRICS_GROUPING_OPTION.NONE:
      return [
        {
          key: "all",
          label: "All Metrics",
          count: metrics.length,
          metrics: sort(metrics),
          color: "default",
        },
      ]

    case METRICS_GROUPING_OPTION.TYPE: {
      const typeGroups = new Map<string, T[]>()
      metrics.forEach((metric) => {
        const category = resolveMetricCategory(
          byManager[metric.metric_manager ?? ""],
          metric.metric_manager,
        )
        if (!typeGroups.has(category)) {
          typeGroups.set(category, [])
        }
        typeGroups.get(category)!.push(metric)
      })
      return Array.from(typeGroups.entries())
        .map(
          ([label, groupMetrics]): MetricGroup<T> => ({
            key: label.toLowerCase().replace(/\s+/g, "-"),
            label,
            count: groupMetrics.length,
            metrics: sort(groupMetrics),
            color: label === "Other" ? "default" : "primary",
          }),
        )
        .sort(sortGroupsByLabel)
    }

    case METRICS_GROUPING_OPTION.CATEGORY: {
      // Functional category from the catalog (distinct from the methodology-based Type grouping).
      const categoryGroups = new Map<string, T[]>()
      metrics.forEach((metric) => {
        const category =
          byManager[metric.metric_manager ?? ""]?.category || "Other"
        if (!categoryGroups.has(category)) {
          categoryGroups.set(category, [])
        }
        categoryGroups.get(category)!.push(metric)
      })
      return Array.from(categoryGroups.entries())
        .map(
          ([label, groupMetrics]): MetricGroup<T> => ({
            key: label.toLowerCase().replace(/\s+/g, "-"),
            label,
            count: groupMetrics.length,
            description: CATEGORY_DESCRIPTIONS[label],
            metrics: sort(groupMetrics),
            color: label === "Other" ? "default" : "primary",
          }),
        )
        .sort(sortGroupsByLabel)
    }

    case METRICS_GROUPING_OPTION.TAG: {
      // A metric appears under each of its tags; metrics with no tags fall
      // into a single "Untagged" group rendered at the bottom.
      const tagGroups = new Map<string, T[]>()
      const untagged: T[] = []
      metrics.forEach((metric) => {
        const tags = metric.tags ?? []
        if (tags.length === 0) {
          untagged.push(metric)
          return
        }
        tags.forEach((tag) => {
          if (!tagGroups.has(tag)) {
            tagGroups.set(tag, [])
          }
          tagGroups.get(tag)!.push(metric)
        })
      })
      const groups: MetricGroup<T>[] = Array.from(tagGroups.entries())
        .map(([tag, groupMetrics]) => ({
          key: getTagGroupKey(tag),
          label: tag,
          count: groupMetrics.length,
          metrics: sort(groupMetrics),
          color: "primary" as const,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

      if (untagged.length > 0) {
        groups.push({
          key: UNTAGGED_GROUP_KEY,
          label: "Untagged",
          count: untagged.length,
          metrics: sort(untagged),
          color: "default",
        })
      }
      return groups
    }

    default:
      return []
  }
}
